import { yupResolver } from "@hookform/resolvers/yup";
import { cn } from "@omni/utils/styles";
import { FC, useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Divider } from "@/components/General/Divider/divider.component";
import { Price } from "@/components/Price/price";
import { useProductDetail } from "@/components/Product/ECommerce/hooks/useProductDetail.hook";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { formatPrice } from "@/utils/price.utils";

import { ProductConfigurationVisualProps } from "../../productConfiguration.types";
import { YieldCalculatorField } from "./components/yieldCalculatorField.component";
import { useYieldFeatureValues } from "./yieldCalculator.helpers";
import {
    YieldCalculatorFormValues,
    YieldCalculatorValidationSchema
} from "./yieldCalculator.schema";
import {
    YieldCalculatorResult,
    YieldConfiguration
} from "./yieldCalculator.types";

const FORM_ID = "yield-calculator-form";

const YieldCalculator: FC<
    ProductConfigurationVisualProps<YieldConfiguration>
> = ({ setConfigurationValid, metaConfiguration: { product } = {} }) => {
    const { t } = useGranite();
    const {
        features = [],
        analytics = undefined,
        price = null
    } = product || {};
    const { updateProductDetail } = useProductDetail();
    const { canShowCalculator, calculateQuantity, yieldLayers, yieldUnit } =
        useYieldFeatureValues({
            features,
            analytics
        });
    const [yieldResult, setYieldResult] = useState<
        YieldCalculatorResult | undefined
    >(undefined);

    useEffect(() => {
        // config is always valid
        setConfigurationValid(true);
    });

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            surface: undefined
        },
        resolver: yupResolver(YieldCalculatorValidationSchema)
    });

    const onSubmit = (data: YieldCalculatorFormValues) => {
        const { quantity } = calculateQuantity(data.surface);
        if (quantity > 0) {
            setYieldResult({
                quantity
            });
            updateProductDetail({
                quantity
            });
        }
    };

    if (!canShowCalculator) {
        return null;
    }

    return (
        <div
            className={cn(
                "tw:bg-white tw:p-4 tw:border tw:rounded-sm tw:border-grey-input tw:mb-4"
            )}
        >
            <p className={cn("tw:font-bold")}>
                <i className={cn("las la-calculator tw:mr-3")}></i>
                {t("yieldCalculator.title")}
            </p>
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <FormProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onSubmit)}
                        >
                            <YieldCalculatorField<YieldCalculatorFormValues>
                                label={t(
                                    "yieldCalculator.surface.label",
                                    yieldUnit
                                )}
                                placeholder={t(
                                    "yieldCalculator.surface.placeholder",
                                    yieldUnit
                                )}
                                name="surface"
                                required
                                unit={yieldUnit}
                                buttonLabel={t(
                                    "yieldCalculator.calculate.buttonLabel"
                                )}
                            />
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
            {yieldResult && (
                <>
                    <p className={cn("tw:mb-0")}>
                        <span className={cn("tw:font-bold tw:mr-2")}>
                            {t(
                                "yieldCalculator.result.label",
                                yieldResult.quantity
                            )}
                        </span>
                    </p>
                    <p>
                        {t(
                            yieldLayers === 1
                                ? "yieldCalculator.result.info.singleLayer"
                                : "yieldCalculator.result.info.multipleLayers",
                            yieldLayers
                        )}
                    </p>
                    {price?.currentPrice && (
                        <>
                            <Divider marginTop="0" />
                            <div className={cn("tw:flex tw:gap-3 tw:mt-3")}>
                                <div className={cn("tw:grow")}>
                                    <span className={cn("tw:font-bold")}>
                                        {t("yieldCalculator.result.totalPrice")}
                                    </span>
                                    <br />
                                    <Price
                                        price={
                                            price.currentPrice *
                                            yieldResult.quantity
                                        }
                                        size="md"
                                    >
                                        <Price.Main
                                            className={cn("tw:text-black")}
                                        />
                                    </Price>
                                </div>
                                <div className={cn("tw:self-end tw:text-sm")}>
                                    {t(
                                        "yieldCalculator.result.calculation",
                                        yieldResult.quantity,
                                        formatPrice(price.currentPrice)
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export { YieldCalculator };
